import { Button } from "@volca/ui/components/ui/button";
import { TOOLBOX } from "@volca/ui/components/ui/empty-machine";
import { EmptyPage } from "@volca/ui/components/ui/empty-page";
import { ErrorState } from "@volca/ui/components/ui/error-state";
import { Skeleton } from "@volca/ui/components/ui/skeleton";
import { Stack } from "@volca/ui/components/ui/stack";
import { Heading, Text } from "@volca/ui/components/ui/text";
import { TEMPLATES_BY_VOLCA } from "./access";
import { useReports } from "./report-query";
import { TemplateCard } from "./template-card";
import { reportError } from "./utils";

export const ReportList = ({
  businessId,
  canAuthor = true,
}: {
  businessId: string;
  /** Whether the empty list may point at New template. */
  canAuthor?: boolean;
}) => {
  const reports = useReports(businessId);
  return (
    <Stack gap={6} className="min-h-0 w-full flex-1">
      {!!reports.data?.length && <Heading level={2}>Agents</Heading>}
      {reports.isPending && (
        <Stack gap={6} className="w-full" aria-busy role="status">
          <span className="sr-only">Loading agents</span>
          <Skeleton className="h-7 w-24" aria-hidden />
          <div
            className="grid grid-cols-1 gap-6 lg:grid-cols-2 2xl:grid-cols-3"
            aria-hidden
          >
            {["one", "two", "three"].map((card) => (
              <Skeleton key={card} className="h-40 rounded-xl" />
            ))}
          </div>
        </Stack>
      )}
      {reports.isError && !reports.data && (
        <ErrorState
          title="Agents could not load"
          description={reportError(reports.error)}
          retrying={reports.isFetching}
        >
          <Button
            variant="secondary"
            disabled={reports.isFetching}
            onClick={() => void reports.refetch()}
          >
            Retry agents
          </Button>
        </ErrorState>
      )}
      {reports.isError && reports.data && (
        <Stack gap={3} align="start">
          <Text role="alert" tone="destructive">
            The agents could not load. {reportError(reports.error)} Try again.
          </Text>
          <Button variant="secondary" onClick={() => void reports.refetch()}>
            Retry agents
          </Button>
        </Stack>
      )}
      {reports.data?.length === 0 && (
        <EmptyPage
          bleed
          art={TOOLBOX}
          title="Build your first agent"
          tagline="Describe the report your business needs"
          className="min-h-0"
          footnote={
            canAuthor
              ? "Select New agent above and attach example files. Save it once, then reuse it with new documents whenever you need a fresh report."
              : TEMPLATES_BY_VOLCA
          }
        />
      )}
      {!!reports.data?.length && (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2 2xl:grid-cols-3">
          {reports.data.map((report) => (
            <TemplateCard key={report.id} report={report} />
          ))}
        </div>
      )}
    </Stack>
  );
};
